import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, type TooltipTipDirection, type TooltipVariant } from './Tooltip';
import './Tooltip.css';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

/** Wraps a single child element and shows a positioned Tooltip on hover/focus. */
export type TooltipTriggerProps = {
  text?: string;
  /** Rich tooltip body. When provided it takes precedence over `text`. */
  content?: ReactNode;
  /** Tooltip surface — `'dark'` (default) or `'light'` (white surface, primary text). */
  variant?: TooltipVariant;
  placement?: TooltipPlacement;
  disabled?: boolean;
  className?: string;
  /** Extra class for the tooltip bubble itself (portaled to body, so `className` on the trigger cannot reach it). */
  popupClassName?: string;
  /**
   * Keep the bubble open while the pointer (or focus) is inside it so links and
   * buttons in `content` can be used. Closing is delayed briefly after leaving
   * the trigger so the pointer can cross the gap into the bubble.
   */
  interactive?: boolean;
  children: ReactElement;
};

/** Grace period (ms) for moving the pointer from the trigger into an interactive bubble. */
const INTERACTIVE_CLOSE_DELAY_MS = 150;

const PLACEMENT_TIP: Record<TooltipPlacement, TooltipTipDirection> = {
  top: 'top-center',
  bottom: 'bottom-center',
  left: 'left-center',
  right: 'right-center',
};

const GAP = 4;

/** Viewport-edge padding so tooltips never touch the edge. */
const VIEWPORT_MARGIN = 8;

function clampToViewport(style: CSSProperties, popupWidth: number, popupHeight: number): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let { top, left } = style as { top: number; left: number };

  if (left + popupWidth > vw - VIEWPORT_MARGIN) left = vw - VIEWPORT_MARGIN - popupWidth;
  if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
  if (top + popupHeight > vh - VIEWPORT_MARGIN) top = vh - VIEWPORT_MARGIN - popupHeight;
  if (top < VIEWPORT_MARGIN) top = VIEWPORT_MARGIN;

  return { ...style, top, left };
}

function computePopupStyle(
  triggerRect: DOMRect,
  popupRect: DOMRect | null,
  placement: TooltipPlacement,
): CSSProperties {
  const popupWidth = popupRect?.width ?? 0;
  const popupHeight = popupRect?.height ?? 0;

  let style: CSSProperties;

  switch (placement) {
    case 'bottom':
      style = {
        top: triggerRect.bottom + GAP,
        left: triggerRect.left + triggerRect.width / 2 - popupWidth / 2,
      };
      break;
    case 'left':
      style = {
        top: triggerRect.top + triggerRect.height / 2 - popupHeight / 2,
        left: triggerRect.left - GAP - popupWidth,
      };
      break;
    case 'right':
      style = {
        top: triggerRect.top + triggerRect.height / 2 - popupHeight / 2,
        left: triggerRect.right + GAP,
      };
      break;
    case 'top':
    default:
      style = {
        top: triggerRect.top - GAP - popupHeight,
        left: triggerRect.left + triggerRect.width / 2 - popupWidth / 2,
      };
      break;
  }

  return clampToViewport(style, popupWidth, popupHeight);
}

/**
 * Hover/focus tooltip that portals to `document.body` so it is not clipped by
 * overflow scrollers, cards, or drawers.
 */
export function TooltipTrigger({
  text = '',
  content,
  variant = 'dark',
  placement = 'top',
  disabled = false,
  className = '',
  popupClassName = '',
  interactive = false,
  children,
}: TooltipTriggerProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  // Pending delayed close for interactive bubbles (cancelled when the pointer
  // or focus lands inside the bubble before it fires).
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const requestClose = useCallback(() => {
    cancelClose();
    if (!interactive) {
      setOpen(false);
      return;
    }
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, INTERACTIVE_CLOSE_DELAY_MS);
  }, [interactive, cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);
  // Offscreen until the layout effect measures the trigger — a `{}` initial
  // style would paint the popup at the viewport's top-left corner for one
  // frame on every open (visible as flicker when re-hovering).
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({ top: -9999, left: -9999 });
  // Where the pointer entered/moved. A wrapped inline trigger (e.g. a
  // multi-line mapped value) renders one client rect per line; anchoring to
  // the union bounding box would place the tooltip at the box's top — which
  // can be scrolled out of view inside an overflow container, floating the
  // tooltip over unrelated UI. Anchor to the hovered line fragment instead.
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const anchorRect = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return null;
    const bounding = trigger.getBoundingClientRect();
    const point = pointerRef.current;
    if (!point) return bounding;
    const rects = Array.from(trigger.getClientRects());
    if (rects.length <= 1) return bounding;
    const hit =
      rects.find((rect) => point.y >= rect.top && point.y <= rect.bottom) ??
      rects.reduce((closest, rect) => {
        const distance = Math.min(Math.abs(rect.top - point.y), Math.abs(rect.bottom - point.y));
        const closestDistance = Math.min(
          Math.abs(closest.top - point.y),
          Math.abs(closest.bottom - point.y),
        );
        return distance < closestDistance ? rect : closest;
      }, rects[0]);
    return hit ?? bounding;
  }, []);

  const updatePosition = useCallback(() => {
    const triggerRect = anchorRect();
    if (!triggerRect) return;
    const popupRect = popupRef.current?.getBoundingClientRect() ?? null;
    setPopupStyle(computePopupStyle(triggerRect, popupRect, placement));
  }, [placement, anchorRect]);

  useLayoutEffect(() => {
    if (!open || disabled) return undefined;
    updatePosition();
    // Second pass after the portal paints so we have real popup dimensions.
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [open, disabled, text, content, placement, updatePosition]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, disabled, updatePosition]);

  const rootClasses = [
    'spark-tooltip-trigger',
    `spark-tooltip-trigger-placement-${placement}`,
    disabled ? 'spark-tooltip-trigger-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const show = open && !disabled;

  // Nested triggers (e.g. a paused button inside a consumer's tooltip): only the
  // innermost active one under the pointer shows, so two bubbles never overlap.
  const coveredByInnerTrigger = useCallback((target: EventTarget | null) => {
    const trigger = triggerRef.current;
    const innermost =
      target instanceof Element ? target.closest('.spark-tooltip-trigger:not(.spark-tooltip-trigger-disabled)') : null;
    return Boolean(trigger && innermost && innermost !== trigger && trigger.contains(innermost));
  }, []);

  // Follow the hovered line while the cursor moves inside a wrapped trigger:
  // reposition only when the pointer crosses into a different line fragment.
  const anchorTopRef = useRef<number | null>(null);
  const handleMouseMove = useCallback(
    (event: { clientX: number; clientY: number; target: EventTarget | null }) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };
      const covered = coveredByInnerTrigger(event.target);
      if (covered !== !open) setOpen(!covered);
      if (!open || disabled) return;
      const trigger = triggerRef.current;
      if (!trigger || trigger.getClientRects().length <= 1) return;
      const rect = anchorRect();
      if (rect && rect.top !== anchorTopRef.current) {
        anchorTopRef.current = rect.top;
        updatePosition();
      }
    },
    [open, disabled, anchorRect, updatePosition, coveredByInnerTrigger],
  );

  return (
    <span
      ref={triggerRef}
      className={rootClasses}
      onMouseEnter={(event) => {
        cancelClose();
        pointerRef.current = { x: event.clientX, y: event.clientY };
        anchorTopRef.current = null;
        setOpen(!coveredByInnerTrigger(event.target));
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        pointerRef.current = null;
        requestClose();
      }}
      onFocus={(e) => {
        cancelClose();
        if (e.currentTarget.matches(':focus-visible') ||
            e.currentTarget.querySelector(':focus-visible')) {
          setOpen(true);
        }
      }}
      onBlur={requestClose}
    >
      {children}
      {show
        ? createPortal(
            <span
              ref={popupRef}
              className={[
                'spark-tooltip-trigger-popup',
                'spark-tooltip-trigger-popup--portal',
                interactive ? 'spark-tooltip-trigger-popup--interactive' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={popupStyle}
              role={interactive ? 'tooltip' : undefined}
              aria-hidden={interactive ? undefined : 'true'}
              onMouseEnter={interactive ? cancelClose : undefined}
              onMouseLeave={interactive ? requestClose : undefined}
              onFocus={interactive ? cancelClose : undefined}
              onBlur={interactive ? requestClose : undefined}
            >
              {content !== undefined && content !== null ? (
                <Tooltip
                  type="slot"
                  variant={variant}
                  tipDirection={PLACEMENT_TIP[placement]}
                  className={['spark-tooltip-trigger-tooltip', popupClassName].filter(Boolean).join(' ')}
                >
                  {content}
                </Tooltip>
              ) : (
                <Tooltip
                  text={text}
                  variant={variant}
                  tipDirection={PLACEMENT_TIP[placement]}
                  className={['spark-tooltip-trigger-tooltip', popupClassName].filter(Boolean).join(' ')}
                />
              )}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}

/** Wrap a native button when tooltip text is resolved outside Button. */
export function wrapIconButtonWithTooltip(
  button: ReactElement,
  text: string,
  options?: { placement?: TooltipPlacement; disabled?: boolean },
): ReactNode {
  return (
    <TooltipTrigger text={text} placement={options?.placement} disabled={options?.disabled}>
      {button}
    </TooltipTrigger>
  );
}
